/**
 * pratica-client.ts — F-003.2 Phase 1 (IMPL-P1-001) + F-003.4 Phase 4 (IMPL-P4-002).
 *
 * Typed fetch wrappers per /api/v1/pratiche:
 *  - POST /api/v1/pratiche                       → crea una BOZZA vuota
 *  - PUT  /api/v1/pratiche/:id/bozza             → autosave dei datiStrutturati
 *  - POST /api/v1/pratiche/:id/pdf/url           → presigned PUT URL per upload S3 (F-003.4)
 *  - POST /api/v1/pratiche/:id/pdf/confirm       → conferma upload, server fa HEAD (F-003.4)
 *  - POST /api/v1/pratiche/:id/submit            → submit con consenso (F-003.4)
 *
 * Convenzioni mirror di profilo-client.ts:
 *  - credentials: 'include' su ogni richiesta (sessione Better Auth)
 *  - PraticaApiError con status + code per error handling tipizzato
 */
import type { ContractDraft } from '@app/shared';

const API = '/api/v1/pratiche';

export interface PraticaView {
  id: string;
  stato: string;
  tipologia: string;
  datiStrutturati: ContractDraft;
  createdAt: string;
  updatedAt: string;
}

/** F-003.4: Response del POST /pdf/url — presigned PUT URL verso IONOS S3. */
export interface PdfUploadUrlResponse {
  uploadUrl: string;
  key: string;
  expiresAt: string;
}

/** F-003.4: Response del POST /pdf/confirm — info PDF post-HEAD server-side. */
export interface PdfConfirmResponse {
  pdfS3Key: string;
  pdfSizeBytes: number;
}

/** F-003.4: Body del POST /submit — consenso esplicito + hash testo accettato. */
export interface SubmitRequestBody {
  consentGiven: true;
  consentTextHash: string;
}

/**
 * F-010: risposta del submit per un PRIVATO — invece di completare, il
 * backend richiede il pagamento e ritorna l'URL del Checkout Stripe.
 */
export interface PraticaPaymentRequired {
  paymentRequired: true;
  checkoutUrl: string;
}

/** Discrimina la risposta submit: pagamento richiesto (privati) vs pratica inviata. */
export function isPaymentRequired(
  result: PraticaView | PraticaPaymentRequired,
): result is PraticaPaymentRequired {
  return (result as PraticaPaymentRequired).paymentRequired === true;
}

export class PraticaApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'PraticaApiError';
    this.status = status;
    this.code = code;
  }
}

interface ApiErrorEnvelope {
  code?: string;
  message?: string;
}

async function parseErrorEnvelope(res: Response): Promise<ApiErrorEnvelope> {
  return (await res
    .json()
    .catch(() => ({ code: 'UNKNOWN', message: 'Errore sconosciuto' }))) as ApiErrorEnvelope;
}

/**
 * F-004 IMPL-P4-001: opts opzionali per creare una BOZZA di adempimento.
 * `tipologia` (PROROGA | RISOLUZIONE | CESSIONE) + `parentId` (pratica madre).
 * Entrambi opzionali: i chiamanti F-003 esistenti (`createBozza()`) restano
 * invariati e continuano a POSTare `{}` (contratto standard).
 */
export interface CreateBozzaOpts {
  tipologia?: string;
  parentId?: string;
}

/**
 * POST /api/v1/pratiche
 * Crea una nuova pratica in stato BOZZA con datiStrutturati vuoti.
 *
 * F-004: accetta opzionalmente `{tipologia, parentId}` per creare un adempimento
 * legato a una pratica madre. Backward compatible — senza argomenti il body è `{}`.
 */
export async function createBozza(opts?: CreateBozzaOpts): Promise<PraticaView> {
  const res = await fetch(API, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts ?? {}),
  });
  if (!res.ok) {
    const env = await parseErrorEnvelope(res);
    throw new PraticaApiError(
      res.status,
      env.code ?? 'CREATE_FAILED',
      env.message ?? 'Impossibile creare la bozza',
    );
  }
  return (await res.json()) as PraticaView;
}

/**
 * PATCH /api/v1/pratiche/:id
 * Autosave dei datiStrutturati (partial ContractDraft).
 * Chiamato da useContractForm dopo debounce di 1.5s.
 *
 * NB: la route backend è `PATCH /:id` (vedi `routes/pratica/update-bozza.ts`).
 * Il client storicamente puntava a `PUT /:id/bozza` — route inesistente → ogni
 * autosave faceva 404 e la bozza restava vuota (il submit poi falliva la
 * validazione completa). I test d'integrazione non lo coglievano perché
 * seedano `datiStrutturati` direttamente nel DB.
 */
/**
 * Allinea i codici fiscali top-level richiesti da `contractFullSchema`
 * (`codiceFiscaleProprietario`/`codiceFiscaleInquilino`) ai CF delle parti
 * raccolti dal form (`locatore.codiceFiscale`/`conduttore.codiceFiscale`).
 *
 * Senza questo, NESSUNA compilazione reale supera la validazione completa al
 * submit: i form (wizard e denso) non hanno campi top-level dedicati per i CF,
 * quindi restavano `undefined` → 422 CONTRACT_INVALID. (I test seedano i dati
 * già completi nel DB e non coglievano il gap.)
 */
function withDerivedCodiciFiscali(draft: ContractDraft): ContractDraft {
  const d = draft as ContractDraft & {
    codiceFiscaleProprietario?: string;
    codiceFiscaleInquilino?: string;
    locatore?: { codiceFiscale?: string };
    conduttore?: { codiceFiscale?: string };
  };
  const proprietario = d.codiceFiscaleProprietario ?? d.locatore?.codiceFiscale;
  const inquilino = d.codiceFiscaleInquilino ?? d.conduttore?.codiceFiscale;
  return {
    ...draft,
    ...(proprietario ? { codiceFiscaleProprietario: proprietario } : {}),
    ...(inquilino ? { codiceFiscaleInquilino: inquilino } : {}),
  } as ContractDraft;
}

export async function updateBozza(id: string, draft: ContractDraft): Promise<PraticaView> {
  const res = await fetch(`${API}/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(withDerivedCodiciFiscali(draft)),
  });
  if (!res.ok) {
    const env = await parseErrorEnvelope(res);
    throw new PraticaApiError(
      res.status,
      env.code ?? 'UPDATE_FAILED',
      env.message ?? 'Impossibile salvare la bozza',
    );
  }
  return (await res.json()) as PraticaView;
}

export function isPraticaApiError(err: unknown): err is PraticaApiError {
  return err instanceof PraticaApiError;
}

/**
 * F-003.4 IMPL-P4-002: POST /api/v1/pratiche/:id/pdf/url
 * Richiede al backend un presigned PUT URL verso IONOS S3.
 * Il browser caricherà direttamente il PDF su S3 con la URL ottenuta.
 */
export async function getPdfUploadUrl(praticaId: string): Promise<PdfUploadUrlResponse> {
  const res = await fetch(`${API}/${praticaId}/pdf/url`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const env = await parseErrorEnvelope(res);
    throw new PraticaApiError(
      res.status,
      env.code ?? 'PDF_URL_FAILED',
      env.message ?? 'Impossibile ottenere la URL di upload',
    );
  }
  return (await res.json()) as PdfUploadUrlResponse;
}

/**
 * F-003.4 IMPL-P4-002: POST /api/v1/pratiche/:id/pdf/confirm
 * Conferma al backend che il PDF è stato caricato su S3.
 * Il backend esegue HEAD per validare contentType + size e persiste i metadati.
 */
export async function confirmPdfUpload(
  praticaId: string,
  body: { sizeBytes: number; etag?: string },
): Promise<PdfConfirmResponse> {
  const res = await fetch(`${API}/${praticaId}/pdf/confirm`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const env = await parseErrorEnvelope(res);
    throw new PraticaApiError(
      res.status,
      env.code ?? 'PDF_CONFIRM_FAILED',
      env.message ?? 'Impossibile confermare il caricamento del PDF',
    );
  }
  return (await res.json()) as PdfConfirmResponse;
}

/**
 * F-003.4 IMPL-P4-002: POST /api/v1/pratiche/:id/submit
 * Submit della pratica con consenso esplicito + hash del testo accettato.
 * Idempotente: se la pratica è già INVIATA, il backend risponde 200 senza side-effect.
 */
export async function submit(
  praticaId: string,
  body: SubmitRequestBody,
): Promise<PraticaView | PraticaPaymentRequired> {
  const res = await fetch(`${API}/${praticaId}/submit`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const env = await parseErrorEnvelope(res);
    throw new PraticaApiError(
      res.status,
      env.code ?? 'SUBMIT_FAILED',
      env.message ?? 'Impossibile inviare la pratica',
    );
  }
  // F-010: privati → { paymentRequired, checkoutUrl }; altri → PraticaView.
  return (await res.json()) as PraticaView | PraticaPaymentRequired;
}

/**
 * F-010: prezzo PRIVATI (cents, stringa) per la pratica — per mostrare
 * "Paga e invia €X". 409 LISTINO_NOT_CONFIGURED se non impostato.
 */
export async function getPraticaPrezzo(praticaId: string): Promise<{ prezzoCents: string }> {
  const res = await fetch(`${API}/${praticaId}/prezzo`, { credentials: 'include' });
  if (!res.ok) {
    const env = await parseErrorEnvelope(res);
    throw new PraticaApiError(
      res.status,
      env.code ?? 'PREZZO_FAILED',
      env.message ?? 'Impossibile ottenere il prezzo',
    );
  }
  return (await res.json()) as { prezzoCents: string };
}
